import { useState } from 'react';
import { useCart } from '@/hooks/useCart';

interface ProductCardProps {
  id: string;
  name: string;
  volume: string;
  price: number;
  image: string;
  imageAlt: string;
  features: string[];
  iconClass: string;
  iconBgClass: string;
  checkColor: string;
}

function ProductCard({
  id,
  name,
  volume,
  price,
  image,
  imageAlt,
  features,
  iconClass,
  iconBgClass,
  checkColor,
}: ProductCardProps) {
  const { addItem, setIsOpen } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    addItem({
      id,
      name,
      price,
      quantity,
      unit: 'unidad',
      image,
      volume,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="bg-background-50 rounded-lg border border-background-200/60 overflow-hidden group hover:border-primary-300/60 transition-all duration-300">
      <div className="aspect-[4/3] overflow-hidden bg-background-200/40">
        <img
          src={image}
          alt={imageAlt}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${iconBgClass}`}>
            <i className={`${iconClass} text-lg`} />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950">{name}</h3>
            <p className="text-foreground-500 text-xs">{volume}</p>
            <p className="text-primary-600 font-heading text-base font-bold mt-1">
              ${price.toLocaleString()} <span className="text-foreground-400 text-xs font-normal">USD</span>
            </p>
          </div>
        </div>
        <div className="space-y-2 mb-5">
          {features.map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm text-foreground-600">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`ri-check-line ${checkColor} text-xs`} />
              </div>
              <span>{f}</span>
            </div>
          ))}
        </div>

        {/* Quantity + Add to cart */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-background-100 rounded-md overflow-hidden border border-background-200/60">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Disminuir cantidad"
            >
              <i className="ri-subtract-line text-xs" />
            </button>
            <span className="w-10 text-center text-sm font-semibold text-foreground-800">{quantity}</span>
            <button
              onClick={() => setQuantity((q) => q + 1)}
              className="w-9 h-9 flex items-center justify-center text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="Aumentar cantidad"
            >
              <i className="ri-add-line text-xs" />
            </button>
          </div>
          <button
            onClick={handleAdd}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              added
                ? 'bg-accent-500 text-background-50'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
          >
            <i className={`${added ? 'ri-check-line' : 'ri-shopping-cart-2-line'} text-sm`} />
            {added ? 'Agregado' : 'Añadir al carrito'}
          </button>
        </div>

        {/* High demand badge */}
        <div className="mt-3 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold">
            <i className="ri-fire-line text-[10px]" />
            Producto de alta demanda
          </span>
          <button
            onClick={() => setIsOpen(true)}
            className="text-primary-600 text-xs font-medium hover:underline cursor-pointer"
          >
            Ver carrito
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PackagingSection() {
  return (
    <section id="presentaciones" className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-5">
              <i className="ri-price-tag-3-line text-sm w-4 h-4 flex items-center justify-center" />
              El Mejor Precio Del Mercado Venezolano
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              Presentaciones <span className="text-primary-500">Disponibles</span>
            </h2>
            <p className="text-foreground-500 text-sm max-w-xl mx-auto">
              Adaptamos nuestra entrega al volumen que su operación requiere. Consulte por precios especiales para compras recurrentes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <ProductCard
              id="tambor-demulsificante"
              name="Tambor Demulsificante"
              volume="55 Galones / 208 Litros"
              price={950}
              image="https://readdy.ai/api/search-image?query=Industrial%2055%20gallon%20blue%20steel%20chemical%20drum%20with%20metal%20lid%20and%20rings%2C%20professional%20product%20photography%20on%20clean%20white%20studio%20background%2C%20soft%20diffused%20lighting%2C%20minimal%20composition%2C%20commercial%20industrial%20grade%20packaging%2C%20high%20detail%20texture%20on%20metal%20surface%2C%20slight%20shadow%20on%20ground&width=800&height=600&seq=tambor-industrial-01&orientation=landscape"
              imageAlt="Tambor metálico industrial de 55 galones para demulsificante RESINVENCA"
              features={[
                'Acero industrial con recubrimiento interior anticorrosivo',
                'Sellado hermético para transporte seguro',
                'Ideal para pedidos pequeños y prueba en campo',
              ]}
              iconClass="ri-archive-line"
              iconBgClass="bg-accent-100 text-accent-600"
              checkColor="text-accent-500"
            />

            <ProductCard
              id="totem-demulsificante"
              name="Totem Demulsificante"
              volume="1.000 Litros"
              price={4500}
              image="https://readdy.ai/api/search-image?query=White%201000%20liter%20IBC%20intermediate%20bulk%20container%20tote%20with%20galvanized%20metal%20cage%20and%20plastic%20pallet%20base%2C%20industrial%20chemical%20storage%20tank%2C%20professional%20product%20photography%20on%20clean%20white%20studio%20background%2C%20soft%20diffused%20lighting%2C%20minimal%20composition%2C%20high%20detail%20showing%20valve%20and%20cage%20structure%2C%20commercial%20grade&width=800&height=600&seq=ibc-container-01&orientation=landscape"
              imageAlt="IBC contenedor industrial de 1000 litros para demulsificante RESINVENCA"
              features={[
                'Tanque plástico de alta resistencia con jaula metálica',
                'Válvula de descarga inferior para fácil dosificación',
                'Mayor volumen, menor costo por litro',
              ]}
              iconClass="ri-bring-to-front"
              iconBgClass="bg-primary-100 text-primary-600"
              checkColor="text-primary-500"
            />
          </div>
        </div>
      </div>
    </section>
  );
}